import React, { useState, useEffect } from "react";
import axios from "axios";
import { FaSearch, FaSync, FaCheckCircle, FaExclamationTriangle, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import API_BASE_URL from "../../../../config/api";

function Guide() {
    const [guides, setGuides] = useState([]);
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [syncSuccess, setSyncSuccess] = useState(false);

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            fetchGuides(page, search);
        }, 500);

        return () => clearTimeout(delayDebounceFn);
    }, [page, search]);

    const fetchGuides = async (currentPage, searchQuery) => {
        try {
            setLoading(true);
            const response = await axios.get(`${API_BASE_URL}/api/guides`, {
                params: {
                    page: currentPage,
                    limit: 9,
                    search: searchQuery
                }
            });

            // Handle the response structure { guides: [], currentPage, totalPages, totalGuides }
            if (response.data && Array.isArray(response.data.guides)) {
                setGuides(response.data.guides);
                setTotalPages(response.data.totalPages);
            } else {
                // Fallback if structure is different (though backend check confirms this structure)
                console.error("Unexpected API response structure:", response.data);
                setGuides([]);
            }
            setError(null);
        } catch (error) {
            console.error("Error fetching guides:", error);
            setError("Failed to load guides. Please try again.");
            setGuides([]);
        } finally {
            setLoading(false);
        }
    };

    const handleSyncGuides = async () => {
        try {
            setLoading(true);
            const response = await axios.post(
                `${API_BASE_URL}/api/guides/sync`
            );
            if (response.status === 200) {
                setSyncSuccess(true);
                setTimeout(() => setSyncSuccess(false), 3000);
                await fetchGuides(page, search);
            }
        } catch (error) {
            console.error("Error syncing guides:", error);
            setError("Failed to sync guides. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleSearchChange = (e) => {
        setSearch(e.target.value);
        setPage(1); // Reset to page 1 on new search
    };

    const handlePrevPage = () => {
        if (page > 1) setPage(page - 1);
    };

    const handleNextPage = () => {
        if (page < totalPages) setPage(page + 1);
    };

    return (
        <div className="min-h-screen p-6 md:p-12 font-sans">
            <div className="max-w-7xl mx-auto">
                <header className="mb-10 flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-800">
                            Guide Directory
                        </h1>
                        <p className="text-gray-500 mt-2">View and sync approved guides</p>
                    </div>
                </header>

                {/* Alerts */}
                {syncSuccess && (
                    <div className="mb-6 p-4 rounded-lg flex items-center gap-3 shadow-sm bg-green-50 text-green-700 border border-green-200 animate-fade-in">
                        <FaCheckCircle />
                        Guides successfully synchronized from faculty data!
                    </div>
                )}

                {error && (
                    <div className="mb-6 p-4 rounded-lg flex items-center gap-3 shadow-sm bg-red-50 text-red-700 border border-red-200 animate-fade-in">
                        <FaExclamationTriangle />
                        {error}
                    </div>
                )}

                {/* Actions Bar */}
                <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 mb-8 flex flex-col md:flex-row gap-4 justify-between items-center">
                    <div className="relative w-full lg:max-w-md">
                        <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search Guide by Name..."
                            value={search}
                            onChange={handleSearchChange}
                            className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#b7202e]/20 focus:border-[#b7202e] transition-all"
                        />
                    </div>

                    <button
                        onClick={handleSyncGuides}
                        disabled={loading}
                        className="flex items-center justify-center gap-2 bg-[#b7202e] hover:bg-[#901a24] text-white px-5 py-2.5 rounded-lg transition-colors shadow-md shadow-red-100 font-medium disabled:opacity-70 disabled:cursor-not-allowed"
                    >
                        <FaSync className={loading ? "animate-spin" : ""} />
                        {loading ? "Syncing..." : "Refresh / Sync"}
                    </button>
                </div>

                {/* Table Section */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    {loading && !guides.length ? (
                        <div className="p-12 text-center text-gray-500 flex flex-col items-center">
                            <div className="w-10 h-10 border-4 border-gray-200 border-t-[#b7202e] rounded-full animate-spin mb-4"></div>
                            <p>Loading guides...</p>
                        </div>
                    ) : guides.length > 0 ? (
                        <>
                            <div className="overflow-x-auto">
                                <table className="w-full text-left border-collapse">
                                    <thead>
                                        <tr className="bg-gray-50 border-b border-gray-200 text-gray-600 text-sm uppercase tracking-wider">
                                            <th className="p-4 font-semibold">Guide ID</th>
                                            <th className="p-4 font-semibold">Name</th>
                                            <th className="p-4 font-semibold">Faculty Abr</th>
                                            <th className="p-4 font-semibold">Email</th>
                                            <th className="p-4 font-semibold">Department</th>
                                            <th className="p-4 font-semibold">Designation</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {guides.map((guide) => (
                                            <tr key={guide._id} className="hover:bg-red-50/30 transition-colors">
                                                <td className="p-4 text-gray-700 font-medium">{guide.guideId}</td>
                                                <td className="p-4 text-gray-800 font-semibold">{guide.name}</td>
                                                <td className="p-4 text-gray-600">{guide.facultyAbr}</td>
                                                <td className="p-4 text-gray-500 text-sm">{guide.email}</td>
                                                <td className="p-4 text-gray-600">
                                                    <span className="inline-block px-2 py-1 bg-gray-100 rounded text-xs font-medium text-gray-600">
                                                        {guide.department}
                                                    </span>
                                                </td>
                                                <td className="p-4 text-gray-600">{guide.designation}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Pagination Controls */}
                            <div className="p-4 border-t border-gray-200 flex items-center justify-between bg-gray-50">
                                <button
                                    onClick={handlePrevPage}
                                    disabled={page === 1 || loading}
                                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    <FaChevronLeft className="w-3 h-3" />
                                    Previous
                                </button>
                                <span className="text-sm text-gray-600">
                                    Page <span className="font-semibold text-gray-900">{page}</span> of <span className="font-semibold text-gray-900">{totalPages}</span>
                                </span>
                                <button
                                    onClick={handleNextPage}
                                    disabled={page === totalPages || loading}
                                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    Next
                                    <FaChevronRight className="w-3 h-3" />
                                </button>
                            </div>
                        </>
                    ) : (
                        <div className="p-12 text-center text-gray-500 bg-gray-50">
                            <FaSearch className="text-4xl mx-auto mb-3 text-gray-300" />
                            <p>No guides found. {search ? "Try a different search." : "Sync from faculty to add guides."}</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default Guide;
